/**
 * MessageBubble Component
 * Displays a single Supabase message with eval tags, flags, ratings, and per-message comments.
 * Supports both the new per-agent escalation flags (custom_message_flags) and legacy eval_items flags.
 */

import { useState, useRef, useEffect, type MouseEvent } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertTriangle, HelpCircle, Brain,
  CheckCheck, MessageCircle, Trash2,
  Loader2, Send, Info, TrendingUp, TrendingDown,
  ThumbsUp, ThumbsDown, Flag, Zap, Bug,
  Settings, Cog, Workflow, Wrench,
} from "lucide-react"
import { ModerationMessage, FlagType, DetectedItem, EvalItemMetaMap } from "../types"
import { formatTimestamp, badgeStyles } from "../utils"
import {
  isIncomingMessage,
  isBotMessage,
  isHITLMessage,
  isModerationFlagItemId,
  moderationCategoryActive,
  moderationCategoryResolved,
  isMessageFlagActive,
  isMessageFlagResolved,
  messageHasAnyActiveFlagCombined,
} from "../utils/conversationUtils"
import {
  getCustomMessageFlagsOnly,
  hasBuiltinFlagColumnSignal,
  getEffectiveBuiltinFlagState,
  isBuiltinFlagId,
} from "../utils/builtinFlagState"
import { cn } from "@/lib/utils"
import { ToMarkdown } from "@/components/ui/tomarkdown"
import type { MessageComment } from "../hooks/useComments"

/** Lookup map of eval item id → labels + moderation role flags from eval_items */
export type EvalItemMap = EvalItemMetaMap

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  "alert-triangle": AlertTriangle,
  "help-circle": HelpCircle,
  "user": Brain,
  "flag": Flag,
  "zap": Zap,
  "bug": Bug,
  "message-circle": MessageCircle,
  "brain-cog": Brain,
  "settings": Settings,
  "cog": Cog,
  "workflow": Workflow,
  "wrench": Wrench,
}

function getFlagIcon(iconName: string) {
  return ICON_MAP[iconName] || Flag
}

// Agent titles may contain markdown/emoji; render them as clean plain text in
// non-markdown contexts like the "Generated by …" tooltip.
function toPlainText(input: string): string {
  return input
    // markdown emphasis / code / strikethrough markers
    .replace(/[*_`~]+/g, "")
    // links [label](url) -> label
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // leading heading / quote markers
    .replace(/^\s{0,3}(#{1,6}|>)\s*/gm, "")
    // emoji and pictographic symbols
    .replace(/[\p{Extended_Pictographic}\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}️]/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim()
}

// Inactive chips stay neutral; semantic color only applies once flagged.
const FLAG_CHIP_BASE = "inline-flex items-center gap-1 h-[22px] px-2 rounded-full border text-[10px] font-medium transition-colors"
const FLAG_RESOLVED = "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900"
const FLAG_INACTIVE = "bg-transparent text-muted-foreground border-border hover:bg-muted hover:text-foreground"

const BUILTIN_FLAG_ACTIVE: Record<string, string> = {
  "high_risk": "bg-destructive/10 text-destructive border-destructive/25 hover:bg-destructive/15",
  "low_confidence": "bg-amber-500/10 text-amber-700 border-amber-500/25 hover:bg-amber-500/15 dark:text-amber-400",
  "asked_human": "bg-violet-500/10 text-violet-700 border-violet-500/25 hover:bg-violet-500/15 dark:text-violet-400",
}

function getFlagColors(flagId: string) {
  return {
    active: BUILTIN_FLAG_ACTIVE[flagId] || "bg-primary/10 text-primary border-primary/25 hover:bg-primary/15",
    resolved: FLAG_RESOLVED,
    inactive: FLAG_INACTIVE,
  }
}

interface MessageBubbleProps {
  message: ModerationMessage
  evalItems?: EvalItemMap
  agentFlags?: AgentEscalationFlag[]
  /** Map of agent id → title, so a bot message can show which agent generated it. */
  agentTitles?: Record<number, string>
  onToggleFlag: (flagType: FlagType, currentItems: DetectedItem[], currentFlags: FlagDetectedItem[] | undefined, message: ModerationMessage) => void
  onResolveAll?: (currentItems: DetectedItem[], currentFlags: FlagDetectedItem[] | undefined, message: ModerationMessage) => void
  onRate?: (rating: "0" | "1", example?: string) => Promise<void>
  comments?: MessageComment[]
  onAddComment?: (text: string) => Promise<void>
  onDeleteComment?: (commentId: string) => Promise<void>
}

export function MessageBubble({
  message,
  evalItems = {},
  agentFlags,
  agentTitles = {},
  onToggleFlag,
  onResolveAll,
  onRate,
  comments = [],
  onAddComment,
  onDeleteComment,
}: MessageBubbleProps) {
  const incoming = isIncomingMessage(message)
  const outgoing = !incoming
  const isBot    = isBotMessage(message)
  const isHITL   = isHITLMessage(message)
  const deliveryStatus = (() => {
    const rawStatus = (message.integration as any)?.status
    return typeof rawStatus === "string" && rawStatus.trim() ? rawStatus : null
  })()
  const shouldShowDeliveryStatus = !(isBot && deliveryStatus === "received")
  const deliveryErrorMessage = (() => {
    const raw = (message.integration as any)?.error_message
    return typeof raw === "string" && raw.trim() ? raw : null
  })()
  const deliveryErrorCode = (() => {
    const raw = (message.integration as any)?.error_code
    return typeof raw === "number" ? raw : null
  })()
  const isDeliveryFailure = deliveryStatus === "failed" || deliveryStatus === "failed_queued" || deliveryStatus === "not_delivered" || deliveryStatus === "cancelled"
  const isDeliverySuccess = deliveryStatus === "delivered" || deliveryStatus === "read"

  const userItems  = message.user_detected_items  || []
  const agentItems = message.agent_detected_items || []
  const msgFlags   = message.custom_message_flags || []
  const customMsgFlags = getCustomMessageFlagsOnly(msgFlags)

  const meta = evalItems

  const moderationItems = incoming ? userItems : isBot ? agentItems : []

  // Separate moderation-flag detections from regular eval tags
  const evalTagItems = userItems.filter(i => !isModerationFlagItemId(i.itemId as number, meta))
  const agentEvalTagItems = agentItems.filter(i => !isModerationFlagItemId(i.itemId as number, meta))

  const useNewFlags =
    (agentFlags && agentFlags.length > 0) ||
    hasBuiltinFlagColumnSignal(message) ||
    msgFlags.length > 0

  // Build the flag definitions to render controls for.
  // If agentFlags were loaded, use them. Otherwise synthesize definitions from whatever
  // flagIds are present on the message itself so flags still show even if the agent config
  // fetch hasn't completed or the agent has no custom flags beyond builtins.
  const BUILTIN_FLAG_DEFS: AgentEscalationFlag[] = [
    { id: "high_risk",       type: "builtin", label: "Risk",       icon: "alert-triangle", detection_prompt: "", enabled: true },
    { id: "low_confidence",  type: "builtin", label: "Low Conf",   icon: "help-circle",    detection_prompt: "", enabled: true },
    { id: "asked_human",     type: "builtin", label: "Ask Human",  icon: "user",           detection_prompt: "", enabled: true },
  ]

  const enabledFlags: AgentEscalationFlag[] = (() => {
    if (agentFlags && agentFlags.length > 0) return agentFlags.filter(f => f.enabled)
    if (customMsgFlags.length > 0) {
      // Synthesize from custom_message_flags only; builtins come from columns
      const known = new Map(BUILTIN_FLAG_DEFS.map(f => [f.id, f]))
      const seen = new Set<string>()
      const result: AgentEscalationFlag[] = []
      for (const mf of customMsgFlags) {
        if (seen.has(mf.flagId)) continue
        seen.add(mf.flagId)
        result.push(known.get(mf.flagId) ?? { id: mf.flagId, type: "custom", label: mf.flagId, icon: "flag", detection_prompt: "", enabled: true })
      }
      // Also include builtin defs so moderators can manually toggle them
      for (const def of BUILTIN_FLAG_DEFS) {
        if (!seen.has(def.id)) result.push(def)
      }
      return result
    }
    return BUILTIN_FLAG_DEFS
  })()

  const hasAnyActiveFlagNew = messageHasAnyActiveFlagCombined(message)

  // Legacy model: check eval_items
  const highRiskFlagged    = moderationCategoryActive(moderationItems, meta, "high_risk")
  const highRiskResolved   = moderationCategoryResolved(moderationItems, meta, "high_risk")
  const lowConfFlagged     = moderationCategoryActive(moderationItems, meta, "low_confidence")
  const lowConfResolved    = moderationCategoryResolved(moderationItems, meta, "low_confidence")
  const askedHumanFlagged  = moderationCategoryActive(moderationItems, meta, "asked_human")
  const askedHumanResolved = moderationCategoryResolved(moderationItems, meta, "asked_human")
  const hasAnyActiveFlagLegacy = highRiskFlagged || lowConfFlagged || askedHumanFlagged

  const hasAnyActiveFlag = useNewFlags ? hasAnyActiveFlagNew : hasAnyActiveFlagLegacy

  // Escalation data
  const fromLevel        = message.escalation_from_level ?? 0
  const toLevel          = message.escalation_to_level   ?? 0
  const escalationReason = message.escalation_reasoning
  const isEscalating     = toLevel > fromLevel
  const isDeescalating   = toLevel < fromLevel
  const hasEscalation    = (isEscalating || isDeescalating || toLevel > 0)


  const [localRating, setLocalRating]     = useState<"0" | "1" | null>(null)
  const [feedbackOpen, setFeedbackOpen]   = useState(false)
  const [feedbackText, setFeedbackText]   = useState("")
  const [submittingRating, setSubmittingRating] = useState(false)

  const effectiveRating = localRating ?? message.rating ?? null

  const handleThumbsUp = () => {
    if (!onRate) return
    setLocalRating("1")
    onRate("1").catch(() => setLocalRating(null))
  }

  const handleThumbsDown = () => {
    setFeedbackText("")
    setFeedbackOpen(true)
  }

  const handleSubmitFeedback = async () => {
    if (!onRate || submittingRating) return
    setSubmittingRating(true)
    setLocalRating("0")
    try {
      await onRate("0", feedbackText.trim() || undefined)
      setFeedbackOpen(false)
      setFeedbackText("")
    } catch {
      setLocalRating(null)
    } finally {
      setSubmittingRating(false)
    }
  }

  const [commentExpanded, setCommentExpanded] = useState(false)
  const [commentText, setCommentText]         = useState("")
  const [savingComment, setSavingComment]     = useState(false)
  const commentInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (commentExpanded && commentInputRef.current) commentInputRef.current.focus()
  }, [commentExpanded])

  const handleAddComment = async () => {
    if (!commentText.trim() || !onAddComment || savingComment) return
    setSavingComment(true)
    try {
      await onAddComment(commentText.trim())
      setCommentText("")
    } finally {
      setSavingComment(false)
    }
  }

  const levelColor = (level: number) => {
    if (level >= 5) return "bg-red-600 text-white border-red-700"
    if (level >= 4) return "bg-orange-500 text-white border-orange-600"
    if (level >= 3) return "bg-amber-400 text-amber-900 border-amber-500"
    if (level >= 2) return "bg-yellow-200 text-yellow-800 border-yellow-300"
    if (level >= 1) return "bg-slate-100 text-slate-700 border-slate-300"
    return "bg-gray-100 text-gray-500 border-gray-300"
  }

  const confidenceBar = (confidence?: number) => {
    if (confidence == null) return null
    const pct = Math.round(confidence * 100)
    const color = pct >= 80 ? "bg-destructive" : pct >= 50 ? "bg-amber-500" : "bg-muted-foreground/50"
    return (
      <div className="flex items-center gap-1 mt-0.5">
        <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
          <div className={cn("h-full rounded-full", color)} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[9px] text-muted-foreground w-6 text-right">{pct}%</span>
      </div>
    )
  }

  const handleMessageLinkClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null
    const anchor = target?.closest("a")
    if (!anchor) return
    const href = anchor.getAttribute("href")
    if (!href) return

    e.preventDefault()
    window.open(anchor.href, "_blank", "noopener,noreferrer")
  }

  return (
    <div className={cn("w-full flex mb-3 mx-1", incoming ? "justify-start" : "justify-end")}>
      <div className={cn("max-w-[78%] min-w-0 space-y-1.5", incoming ? "mr-auto ml-1" : "ml-auto mr-1")}>

        {/* ── Message bubble ── */}
        <div className={cn(
          "px-3.5 py-2 text-[13px] shadow-xs",
          incoming
            ? "bg-muted text-foreground rounded-2xl rounded-bl-md"
            : "bg-primary/10 text-foreground border border-primary/15 rounded-2xl rounded-br-md",
          incoming && hasAnyActiveFlag && "ring-2 ring-destructive/40"
        )}>
          <div className={cn(
            "wrap-break-word wrap-anywhere [word-break:break-word] text-[13px] [&_p]:my-0.5 [&_ul]:my-1 [&_ol]:my-1 [&_pre]:my-1 [&_code]:text-[12px]",
            outgoing && "[&_a]:text-primary [&_a]:underline [&_code]:bg-primary/15 [&_pre]:bg-primary/10"
          )} onClick={handleMessageLinkClick}>
            <ToMarkdown>{message.message || ""}</ToMarkdown>
          </div>

          {/* ── INCOMING: Escalation summary ── */}
          {incoming && hasEscalation && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                {(isEscalating || isDeescalating) ? (
                  <Badge className={cn("text-[10px] h-5 px-1.5 border font-medium gap-1", levelColor(toLevel))}>
                    {isEscalating
                      ? <TrendingUp className="h-2.5 w-2.5" />
                      : <TrendingDown className="h-2.5 w-2.5" />}
                    {fromLevel} → {toLevel}
                  </Badge>
                ) : (
                  <Badge className={cn("text-[10px] h-5 px-1.5 border font-medium", levelColor(toLevel))}>
                    Severity {toLevel}
                  </Badge>
                )}

                {escalationReason && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-blue-500 hover:text-blue-700">
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px] text-xs">{escalationReason}</TooltipContent>
                  </Tooltip>
                )}
              </div>

            </div>
          )}

          {/* ── INCOMING: Eval item tags (non-moderation), kept inside bubble ── */}
          {incoming && evalTagItems.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {evalTagItems.map(item => {
                const info = evalItems[item.itemId as number]
                const label = info?.name ?? `Item ${item.itemId}`
                return (
                  <Tooltip key={item.itemId}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] h-auto py-0.5 px-1.5 cursor-default border font-normal",
                            levelColor(item.severity ?? 0)
                          )}
                        >
                          {label}
                          <span className="ml-1 font-bold">{item.severity}</span>
                        </Badge>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[240px] space-y-1 text-xs p-2">
                      <p className="font-medium">{label}</p>
                      {item.confidence != null && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Confidence</p>
                          {confidenceBar(item.confidence)}
                        </div>
                      )}
                      {item.reasoning && <p className="text-[11px] text-foreground/80 pt-0.5">{item.reasoning}</p>}
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </div>
          )}


          {/* ── Metadata row ── */}
          <div className="flex items-center gap-1.5 mt-1.5 pt-1 border-t border-foreground/5 text-[10px] text-muted-foreground">
            <span className="tabular-nums">{formatTimestamp(message.created_at)}</span>

            {isBot && (() => {
              const rawAgentName = message.agent != null ? agentTitles[message.agent] : undefined
              const agentName = rawAgentName ? toPlainText(rawAgentName) : undefined
              const badge = (
                <Badge variant="outline" className={cn(badgeStyles.msgOutgoing, agentName && "cursor-help")}>
                  <Brain className="h-2.5 w-2.5 mr-0.5" />Bot
                </Badge>
              )
              if (!agentName) return badge
              return (
                <Tooltip>
                  {/* span wrapper: Badge is a plain fn component and can't take a ref */}
                  <TooltipTrigger asChild>
                    <span className="inline-flex">{badge}</span>
                  </TooltipTrigger>
                  <TooltipContent>Generated by {agentName}</TooltipContent>
                </Tooltip>
              )
            })()}

            {isHITL && (
              <Badge variant="outline" className={cn(badgeStyles.msgOutgoing, "whitespace-nowrap max-w-[120px] truncate")}>
                {message.senderContact?.name || message.sender || "Human"}
              </Badge>
            )}

            {outgoing && message.channel === "telerivet" && deliveryStatus && shouldShowDeliveryStatus && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px]",
                      isDeliveryFailure
                        ? "bg-red-50 text-red-700 border-red-300"
                        : isDeliverySuccess
                        ? "bg-green-50 text-green-700 border-green-300"
                        : "bg-amber-50 text-amber-700 border-amber-300"
                    )}
                  >
                    {deliveryStatus.replace(/_/g, " ")}
                  </Badge>
                </TooltipTrigger>
                {isDeliveryFailure && (deliveryErrorMessage || deliveryErrorCode != null) && (
                  <TooltipContent className="max-w-[320px] text-xs">
                    {deliveryErrorMessage || "Delivery failed"}
                    {deliveryErrorCode != null ? ` (code: ${deliveryErrorCode})` : ""}
                  </TooltipContent>
                )}
              </Tooltip>
            )}

            {/* Rating buttons — bot messages only */}
            {isBot && onRate && (
              <div className="flex items-center gap-1 ml-auto" style={{ opacity: 1 }}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleThumbsUp}
                      disabled={submittingRating}
                      className={cn(
                        "p-1 rounded-md transition-all",
                        effectiveRating === "1"
                          ? "text-green-600 bg-green-200 shadow-xs"
                          : "text-green-400 hover:text-green-600 hover:bg-green-100/80"
                      )}
                    >
                      <ThumbsUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Good response</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleThumbsDown}
                      disabled={submittingRating}
                      className={cn(
                        "p-1 rounded-md transition-all",
                        effectiveRating === "0"
                          ? "text-red-600 bg-red-200 shadow-xs"
                          : "text-red-400 hover:text-red-600 hover:bg-red-100/80"
                      )}
                    >
                      <ThumbsDown className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Bad response</TooltipContent>
                </Tooltip>
              </div>
            )}

            {onAddComment && (
              <button
                onClick={() => setCommentExpanded(!commentExpanded)}
                className={cn(
                  "flex items-center gap-0.5 px-1 rounded hover:bg-black/5 transition-colors",
                  commentExpanded && "bg-amber-100 text-amber-700",
                  comments.length > 0 && !commentExpanded && "text-amber-600"
                )}
              >
                <MessageCircle className="h-2.5 w-2.5" />
                {comments.length > 0 && <span className="font-medium">{comments.length}</span>}
              </button>
            )}
          </div>
        </div>

        {/* ── Feedback dialog ── */}
        <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Provide Feedback</DialogTitle>
              <DialogDescription>Help us improve by providing a better response example</DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="What would be a better response?"
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              className="min-h-[100px] text-sm"
              onKeyDown={e => { if (e.key === "Enter" && e.shiftKey === false && e.ctrlKey) handleSubmitFeedback() }}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setFeedbackOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitFeedback} disabled={submittingRating}>
                {submittingRating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting…</> : "Submit Feedback"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* ── FLAG CONTROLS — incoming only ── */}
        {incoming && (
          <div className="flex items-center gap-1 flex-wrap mt-0.5">
            {useNewFlags ? (
              <>
                {enabledFlags.map(flag => {
                  const IconComp = getFlagIcon(flag.icon)
                  const isBuiltin = isBuiltinFlagId(flag.id)
                  const eff = isBuiltin ? getEffectiveBuiltinFlagState(message, flag.id) : null
                  const isFlaggedNow = isBuiltin ? !!eff?.flagged : isMessageFlagActive(customMsgFlags, flag.id)
                  const isResolvedNow = isBuiltin ? !!eff?.resolved : isMessageFlagResolved(customMsgFlags, flag.id)
                  const colors = getFlagColors(flag.id)
                  return (
                    <button
                      key={flag.id}
                      onClick={() => onToggleFlag(flag.id, userItems, msgFlags, message)}
                      className={cn(
                        FLAG_CHIP_BASE,
                        isFlaggedNow ? colors.active : isResolvedNow ? colors.resolved : colors.inactive
                      )}
                    >
                      <IconComp className="h-2.5 w-2.5 shrink-0" />
                      {isResolvedNow && !isFlaggedNow ? `✓ ${flag.label}` : flag.label}
                    </button>
                  )
                })}
                {hasAnyActiveFlagNew && onResolveAll && (
                  <button
                    onClick={() => onResolveAll(userItems, msgFlags, message)}
                    className={cn(FLAG_CHIP_BASE, FLAG_RESOLVED)}
                  >
                    <CheckCheck className="h-2.5 w-2.5 shrink-0" />
                    Resolve All
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => onToggleFlag("high_risk", userItems, undefined, message)}
                  className={cn(
                    FLAG_CHIP_BASE,
                    highRiskFlagged ? getFlagColors("high_risk").active
                      : highRiskResolved ? FLAG_RESOLVED : FLAG_INACTIVE
                  )}
                >
                  <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
                  {highRiskResolved && !highRiskFlagged ? "✓ Risk" : "Risk"}
                </button>
                <button
                  onClick={() => onToggleFlag("low_confidence", userItems, undefined, message)}
                  className={cn(
                    FLAG_CHIP_BASE,
                    lowConfFlagged ? getFlagColors("low_confidence").active
                      : lowConfResolved ? FLAG_RESOLVED : FLAG_INACTIVE
                  )}
                >
                  <HelpCircle className="h-2.5 w-2.5 shrink-0" />
                  {lowConfResolved && !lowConfFlagged ? "✓ Low Conf" : "Low Conf"}
                </button>
                <button
                  onClick={() => onToggleFlag("asked_human", userItems, undefined, message)}
                  className={cn(
                    FLAG_CHIP_BASE,
                    askedHumanFlagged ? getFlagColors("asked_human").active
                      : askedHumanResolved ? FLAG_RESOLVED : FLAG_INACTIVE
                  )}
                >
                  <Brain className="h-2.5 w-2.5 shrink-0" />
                  {askedHumanResolved && !askedHumanFlagged ? "✓ Asked Human" : "Asked Human"}
                </button>
                {hasAnyActiveFlagLegacy && onResolveAll && (
                  <button
                    onClick={() => onResolveAll(userItems, undefined, message)}
                    className={cn(FLAG_CHIP_BASE, FLAG_RESOLVED)}
                  >
                    <CheckCheck className="h-2.5 w-2.5 shrink-0" />
                    Resolve All
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Per-message comments ── */}
        {commentExpanded && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5 space-y-1.5">
            {comments.length > 0 && (
              <div className="space-y-1 max-h-[120px] overflow-y-auto">
                {comments.map(c => (
                  <div key={c.id} className="flex items-start gap-1 group">
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-medium text-amber-700">{c.author_name}</span>
                      <span className="text-[9px] text-muted-foreground ml-1">{formatTimestamp(c.created_at)}</span>
                      <p className="text-[11px] text-foreground/80 line-clamp-3">{c.text}</p>
                    </div>
                    {onDeleteComment && (
                      <button
                        onClick={() => onDeleteComment(c.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0 mt-0.5"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-1">
              <Input
                ref={commentInputRef}
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAddComment() } }}
                placeholder="Add a comment..."
                className="h-6 text-[11px] flex-1 bg-background"
              />
              <Button
                size="icon"
                onClick={handleAddComment}
                disabled={!commentText.trim() || savingComment}
                className="h-6 w-6 shrink-0 rounded-full"
              >
                {savingComment ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
